import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import createFakeEvent from '../../lib/createFakeEvent';
import Notice from '../Notice/Notice';
/**
 * `Switch` is an alternative to the {@link Checkbox} component for toggling a state on or off.
 *
 * Switch should be used over {@link Checkbox} components when the toggling has an immediate effect, e.g. when the toggle
 * state is automatically saved. If the user has to click a **Save** button before the toggle takes effect, use a
 * {@link Checkbox} component instead.
 *
 * An optional notice can be displayed below the input field. See {@link Notice} for the different types.
 *
 * `Switch` also supports an indeterminate state by setting *indeterminate* to `true`. Clicking on an indeterminate
 * switch will always set the state to *checked*.
 *
 * @property {string} className The class name of this component. Will be appended to "core-switch". In addition the
 * classes "checked", "disabled" and "indeterminate" will be appended depending on their prop values.
 * @property {boolean} checked If set to `true`, this component will be toggled on. Defaults to `false`.
 * @property {boolean} disabled If set to `true`, this component will be disabled and the state cannot be changed by the
 * user. Defaults to `false`.
 * @property {node} errorMessage An error {@link Notice} that will be shown below the component. Defaults to no Notice.
 * @property {boolean} indeterminate If set to `true`, the state of this component will be indeterminate and the toggle
 * will be displayed half way between on and off. You can use this state to represent a group of states where all of the
 * states do not have the same value.
 * @property {node} infoMessage An info Notice that will be shown below the component. Defaults to no Notice.
 * @property {string} label The label of your component. Default to no label.
 * @property {function} onChange The callback function that will be called whenever the user has changed the state of this component.
 * @property {node} successMessage A success Notice that will be shown below the component. Defaults to no Notice.
 * @property {node} warningMessage A warning Notice that will be shown below the component. Defaults to no Notice.
 *
 * @see Checkbox, Notice
 */
export default class Switch extends PureComponent {

  static displayName = 'Switch';

  static propTypes = {
    className: PropTypes.string,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.node,
    indeterminate: PropTypes.bool,
    infoMessage: PropTypes.node,
    label: PropTypes.string,
    onChange: PropTypes.func,
    successMessage: PropTypes.node,
    warningMessage: PropTypes.node,
  };

  onChange = e => {
    if (!this.props.disabled && this.props.onChange) {
      this.props.onChange(createFakeEvent(!this.props.checked, e));
    }
  };

  render() {
    const {checked, className, disabled, errorMessage, indeterminate, infoMessage, label, successMessage, warningMessage, ...rest } = this.props;
    const cls = cx('switch-wrapper', {
      checked: checked || indeterminate,
      disabled,
      indeterminate,
    });

    return (
      <div {...rest} className={cx('core-switch', className,{'has-errors': errorMessage || warningMessage || successMessage || infoMessage})} onClick={this.onChange}>
        <div className={cls}>
          <div className="switch-toggle">
            <div className="switch-toggle-indicator"/>
          </div>
          {label && <span className="switch-title">{label}</span>}

        </div>
        <Notice
          errorMessage={errorMessage}
          warningMessage={warningMessage}
          successMessage={successMessage}
          infoMessage={infoMessage}
          />
      </div>
    );
  }

}
